import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Bell, Calendar, Clock, User, Check, X, Loader2, 
  AlertCircle, ChevronRight, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SlotRecoveryMessages = ({ userId, onSlotClaimed }) => {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    const token = localStorage.getItem('token');
    try {
      setLoading(true);
      const res = await axios.get(`${API}/slot-recovery/pending-messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimSlot = async (message) => {
    const token = localStorage.getItem('token');
    try {
      setProcessing(message.id);
      const res = await axios.post(`${API}/slot-recovery/claim`, {
        notification_id: message.notification_id,
        message_id: message.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Appuntamento spostato con successo!');
      loadMessages();
      if (onSlotClaimed) onSlotClaimed(res.data);
    } catch (error) {
      console.error('Error claiming slot:', error);
      toast.error(error.response?.data?.detail || 'Errore nella prenotazione');
    } finally {
      setProcessing(null);
    }
  };

  const handleDismiss = async (messageId) => {
    const token = localStorage.getItem('token');
    try {
      setProcessing(messageId);
      await axios.post(`${API}/slot-recovery/message/${messageId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Notifica ignorata');
    } catch (error) {
      console.error('Error dismissing:', error);
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    return `${days[date.getDay()]} ${date.getDate()}/${date.getMonth() + 1}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-[#2A9FCB]" />
      </div>
    );
  }

  const availableMessages = messages.filter(m => m.still_available && m.status !== 'dismissed');

  if (availableMessages.length === 0) {
    return null; // Non mostrare nulla se non ci sono messaggi
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 text-[#10B981]">
        <Bell className="w-5 h-5" />
        <h3 className="font-bold">Slot Disponibili</h3>
        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
          {availableMessages.length} nuovo/i
        </span>
      </div>

      {/* Messages */}
      <div className="space-y-3">
        {availableMessages.map(message => (
          <div 
            key={message.id}
            className="rounded-xl p-5 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 shadow-sm"
          >
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-white" />
              </div>

              {/* Content */}
              <div className="flex-1">
                <h4 className="font-bold text-green-800 mb-1">
                  🎉 Si è liberato un appuntamento!
                </h4>
                
                <div className="bg-white rounded-lg p-3 my-3 border border-green-100">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-green-600" />
                      <span className="font-semibold">{formatDate(message.freed_date)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-green-600" />
                      <span className="font-semibold">{message.freed_time}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <User className="w-4 h-4 text-green-600" />
                      <span>{message.doctor_name}</span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-green-700 mb-4">
                  Vuoi anticipare la tua visita a questo orario?
                </p>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => handleClaimSlot(message)}
                    disabled={processing === message.id}
                    className="flex-1 py-2.5 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid={`claim-slot-${message.id}`}
                  >
                    {processing === message.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Sì, prenota questo slot
                  </button>
                  <button
                    onClick={() => handleDismiss(message.id)}
                    disabled={processing === message.id}
                    className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                    data-testid={`dismiss-slot-${message.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Urgency indicator */}
            <div className="mt-3 flex items-center gap-2 text-xs text-amber-600">
              <AlertCircle className="w-3 h-3" />
              <span>Lo slot è disponibile fino ad esaurimento - primo arrivato, primo servito!</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SlotRecoveryMessages;
